import {
  EvidenceProfileSchema,
  SourceCoverageSchema,
  type EvidenceProfile,
  type SourceCoverage,
} from './evidence-profile';
import { z } from 'zod';
import {
  FINDING_PATH_CLASSIFICATION,
  FindingFileClassSchema,
} from './finding-path-class';
import {
  ScannerToolEvidenceSchema,
  type ScannerToolEvidence,
} from './scanner-tool-evidence';

// ---------------------------------------------------------------------------
// Durable scan findings: the STABLE problem, and the IMMUTABLE occurrence.
//
// This is deliberately NOT the `findingDigest` that rides on the `repo_scans`
// row. That digest is a display projection: it is capped at sixty records and
// eight kilobytes because it travels as an HTTP header, and — more
// consequentially — it collapses every hit of one rule in one file into a
// single record before it counts anything. Two hits become one, and the second
// is not recoverable from it at any later date.
//
// A capture is produced from the analyser's own finding list, before that
// collapse and before that cap, and is persisted through its own transport.
// The two can therefore disagree about how many findings a scan saw, and when
// they do the capture is the one that counted honestly.
// ---------------------------------------------------------------------------

/** Bumped when an identity rule below changes. Never averaged across. */
/**
 * The identity encoding a capture was minted under.
 *
 * `1` did not include the reporting tool in an occurrence's content key.
 * `2` does, because the tool decides which rules-provenance applies to a
 * finding and therefore decides its verdict.
 *
 * Versioned rather than switched, because both must keep working at once:
 * a capture stored under `1` must stay replayable — a pending graph binding
 * can only be repaired by replaying the ORIGINAL bundle, and recomputing its
 * ids under `2` would reject the very payload that repairs it. And a rescan
 * encoded under `2` must be a DIFFERENT capture, not the same id carrying a
 * different manifest, which is a conflict nobody can resolve.
 */
export const OCCURRENCE_ENCODING_V1 = 1 as const;
export const OCCURRENCE_ENCODING_V2 = 2 as const;
/**
 * `3` encodes every nullable field with a TYPE TAG instead of a sentinel
 * string.
 *
 * Under `1` and `2` an unrecorded tool version was written as the literal
 * `\u0000unknown`, and a tool whose version IS that string wrote the same
 * bytes. Both are schema-valid, so an unknown version could be rewritten as
 * a recorded one after parking and before the first ingest — the capture id,
 * the config version and the manifest all unchanged — and the comparison
 * then dropped `tool-version-unrecorded` and licensed absence claims on both
 * sides. `sha256` aliased `\u0000no-hash` the same way.
 *
 * `canonicalOptional` distinguishes them structurally: `null` is `~` and a
 * string is its own length followed by its bytes, so no string can encode
 * like the absence of one.
 */
export const OCCURRENCE_ENCODING_V3 = 3 as const;
/**
 * `4` names the DIALECT in the config version, which `3` did not.
 *
 * `captureConfigVersion` writes each tool into a length-prefixed list. `2`
 * put the version in as itself and `3` puts a type tag in the same slot, so a
 * `2` capture whose tool version is literally `3~9.0` produced byte for byte
 * the config version of a `3` capture whose version is `9.0` — one string for
 * two readings, and a comparison that trusted it saw one configuration.
 *
 * Prefixing the config version fixes that, and it CHANGES the bytes, so it is
 * a new encoding rather than an edit to `3`. It was briefly made under `3`
 * itself, which would have left every capture already committed under that
 * dialect unrecoverable: recovery selects by number, so it would have rebuilt
 * a different config version, id and manifest and been refused as evidence
 * the upload never committed to.
 */
export const OCCURRENCE_ENCODING_V4 = 4 as const;
export type OccurrenceEncoding =
  | typeof OCCURRENCE_ENCODING_V1
  | typeof OCCURRENCE_ENCODING_V2
  | typeof OCCURRENCE_ENCODING_V3
  | typeof OCCURRENCE_ENCODING_V4;

/**
 * The sentinels `1` and `2` wrote for an absent value.
 *
 * Retained because captures stored under those encodings must still verify
 * and replay byte for byte, and because a value that EQUALS one of them in
 * such a capture is ambiguous and has to be read as unrecorded.
 */
export const LEGACY_UNKNOWN_VERSION = '\u0000unknown';
export const LEGACY_NO_HASH = '\u0000no-hash';
const LEGACY_RULES_UNRECORDED = '\u0000rules-unrecorded';
const LEGACY_TOTAL_UNRECORDED = '\u0000total-unrecorded';
const LEGACY_TRUNCATION_UNRECORDED = '\u0000truncation-unrecorded';

/**
 * A value that a pre-`3` capture cannot distinguish from "not recorded".
 *
 * Read conservatively: under `1` and `2` the sentinel and a literal equal to
 * it encode identically, so a stored capture carrying it establishes nothing
 * about the value. Under `3` the two are distinct and the value stands.
 */
export function recordedOrUnknown(
  value: string | null | undefined,
  encoding: number,
  sentinel: string,
): string | null {
  if (value === null || value === undefined) return null;
  if (encoding >= OCCURRENCE_ENCODING_V3) return value;
  return value === sentinel ? null : value;
}

/** What new captures are minted under. */
export const FINDING_CAPTURE_VERSION = OCCURRENCE_ENCODING_V4;

/**
 * The encoding a capture id was minted under, read off the id itself.
 *
 * `null` when the id does not name one. Recovery needs it: a payload accepted
 * under an older encoding must be rebuilt under THAT one, or the bundle it
 * produces has a different id and manifest and the ingest refuses it as
 * somebody else's evidence — for ever, since no retry changes the answer.
 */
export function encodingOfCaptureId(
  captureId: string,
): OccurrenceEncoding | null {
  const match = /^cap-v(?<version>\d+):/u.exec(captureId);
  const named = Number(match?.groups?.['version'] ?? Number.NaN);
  if (named === OCCURRENCE_ENCODING_V1) return OCCURRENCE_ENCODING_V1;
  if (named === OCCURRENCE_ENCODING_V2) return OCCURRENCE_ENCODING_V2;
  if (named === OCCURRENCE_ENCODING_V3) return OCCURRENCE_ENCODING_V3;
  return named === OCCURRENCE_ENCODING_V4 ? OCCURRENCE_ENCODING_V4 : null;
}

/**
 * Whether a capture's own tool attribution may be TRUSTED.
 *
 * Only under `2`. Under `1` the tool sat outside the content key and the
 * manifest, so two payloads differing only in which analyser reported which
 * finding encoded identically — a legacy capture cannot be asked which tool
 * found something and believed, and every finding in one is therefore
 * unreadable for rules purposes rather than readable on trust.
 */
export function toolAttributionIsTrusted(version: number): boolean {
  return version >= OCCURRENCE_ENCODING_V2;
}

/**
 * Occurrences one capture may persist.
 *
 * Its own bound, roughly an order of magnitude above the digest's sixty. The
 * point is not that it is large but that it is INDEPENDENT: a reader who wants
 * more than the digest's page has somewhere to go, and the retrieval page is a
 * third bound again, so "this page fitted" can never be read as "this is all
 * there was".
 */
export const MAX_CAPTURE_OCCURRENCES = 2000;
/** Recovery of older payloads must retain the bound they were committed with. */
export const LEGACY_CAPTURE_OCCURRENCES = 500;

export const FINDING_SEVERITIES = ['error', 'warning', 'info'] as const;
export const FindingSeveritySchema = z.enum(FINDING_SEVERITIES);
export type FindingSeverity = z.infer<typeof FindingSeveritySchema>;

export const FINDING_CATEGORIES = [
  'lint',
  'security',
  'complexity',
  'dead-code',
  'vulnerability',
  'best-practice',
  'type-safety',
] as const;
export const FindingCategorySchema = z.enum(FINDING_CATEGORIES);
export type FindingCategory = z.infer<typeof FindingCategorySchema>;

export const SUPPRESSION_SCOPES = [
  'line',
  'next-line',
  'block',
  'file',
  'ambiguous',
] as const;

/**
 * Length-prefixed encoding of one component of a composite identity.
 *
 * With a plain separator, `['a:b']` and `['a','b']` encode identically, so two
 * different findings could share one id — and a shared id here silently merges
 * two problems into one, which is invisible from the outside. Prefixing each
 * part with its own byte length leaves every part free to contain any
 * character at all.
 */
export function canonicalString(value: string): string {
  return `${String(value.length)}~${value}`;
}

/**
 * `null` is a distinct encoding, not a short string.
 *
 * A bare `'0~'` for null is exactly what `canonicalString('')` produces, so a
 * finding whose message the analyser did not report and one it reported as
 * empty would share a content key — two different sightings collapsed into
 * one, which is the failure the length prefix exists to prevent.
 */
export function canonicalOptional(value: string | null): string {
  return value === null ? '~' : canonicalString(value);
}

/**
 * A list, encoded so no two different lists can collide.
 *
 * Joining with a separator is not enough: `['a/','b/']` and `['a/,b/']` both
 * render `a/,b/`, so an area built from two prefixes and an area built from
 * one prefix that happens to contain a comma would share a scope key — and a
 * comparison would then be recorded against the wrong boundary. Each member
 * is length-prefixed in its own right and the count is encoded too, which
 * leaves every member free to contain any character at all.
 */
export function canonicalList(values: readonly string[]): string {
  return [
    canonicalString(String(values.length)),
    ...values.map((value) => canonicalString(value)),
  ].join('');
}

// ---------------------------------------------------------------------------
// Tool completeness — per tool, because the suite's aggregate cannot answer it
// ---------------------------------------------------------------------------

/**
 * What ONE analyser did during one scan.
 *
 * `ok` means it ran to completion, so a category it covers having no findings
 * is a real clean reading. `skipped` means it was not installed or its
 * preconditions were unmet, and `failed` means it was installed and broke.
 * Only the first of the three licenses the sentence "there is nothing there".
 */
/**
 * What ONE configuration-file record may carry, as numbers the producer can
 * enforce too.
 *
 * Inline in the schema, the only thing that knew them was the parse at the
 * far end — which runs after the graph has been accepted, where a refusal is
 * permanent. A config path of 600 characters is an ordinary path on disk.
 */
export const MAX_RULE_FILE_PATH = 512;
export const MAX_RULE_FILE_STATE = 64;
export const MAX_RULE_FILE_SHA = 64;

export const ToolCompletenessSchema = z
  .object({
    evidence: ScannerToolEvidenceSchema.optional(),
    name: z.string().min(1).max(64),
    status: z.enum(['ok', 'skipped', 'failed']),
    /** How many findings this tool contributed, pre-cap. */
    findingCount: z.number().int().nonnegative(),
    /**
     * The tool's own version, when the scan could determine it.
     *
     * `null` is UNKNOWN and never "same as last time". Two captures whose
     * tool versions are both unknown have not thereby agreed: a rule set can
     * change under a fixed tool name, and a finding that disappears because
     * a rule was retired is not a finding anybody fixed. The comparison says
     * so with its own named blocker rather than treating null as a match.
     */
    version: z.string().min(1).max(64).nullable().default(null),
    /**
     * Whether THIS tool's effective rules were observed by the scan.
     *
     * Per tool, because the suite's aggregate cannot answer it and a shared
     * answer is far too blunt: one Python tool configured in a format the
     * observer does not parse made every finding in the capture unknown,
     * including the scanner's own suppression audit, whose rules are not in
     * question at all. A comparison can only say "this rule set may have
     * moved" about the tool it actually applies to.
     *
     * `null` is NOT RECORDED — a capture written before this existed — and
     * is read conservatively as unobserved, never as observed.
     */
    rulesObserved: z.boolean().nullable().default(null),
    /**
     * WHY the rules were or were not established, per config file.
     *
     * The capture is durable evidence; the graph is not, and the file-level
     * states lived only there. A reader holding a capture whose graph has
     * been reclaimed could see THAT the rules were undecided and nothing
     * about which file, or why — which is not enough to act on, and not
     * enough to tell an unparsed format from a file nobody could read.
     *
     * Bounded deliberately: paths and states, no hashes. The digest already
     * carries the content identity; this carries the explanation.
     */
    rulesFiles: z
      .array(
        z
          .object({
            path: z.string().min(1).max(MAX_RULE_FILE_PATH),
            state: z.string().min(1).max(MAX_RULE_FILE_STATE),
            /**
             * The content hash, where one was taken.
             *
             * Carried so a retained entry is verifiable rather than merely
             * asserted: a path and a state alone can be rewritten without
             * anything noticing, and this is the explanation a reader acts
             * on when the rules are undecided.
             */
            sha256: z.string().max(64).nullable().default(null),
          })
          .readonly(),
      )
      .max(64)
      .default([]),
    /**
     * How many config files the observer ACTUALLY looked at for this tool.
     *
     * Separate from the retained list, which is bounded. A silent slice made
     * sixty-five files whose only unresolved entry was the sixty-fifth read
     * as a complete and entirely clean explanation — the opposite of what had
     * happened.
     */
    rulesFilesTotal: z.number().int().nonnegative().nullable().default(null),
    /**
     * Whether the retained list is short of `rulesFilesTotal`.
     *
     * `null` is NOT RECORDED — a capture written before this accounting
     * existed — and must not be read as "nothing was dropped". Defaulting it
     * to `false`/`0` made an unrecorded account look like a complete one,
     * which is the same false reassurance the silent slice gave.
     */
    rulesFilesTruncated: z.boolean().nullable().default(null),
  })
  .readonly()
  .refine(
    (tool) =>
      tool.evidence === undefined ||
      (tool.status === 'ok') === (tool.evidence.outcome === 'ok'),
    { message: 'Tool status contradicts its execution evidence' },
  )
  .refine(
    (tool) =>
      tool.rulesFilesTotal === null ||
      tool.rulesFilesTotal >= tool.rulesFiles.length,
    { message: 'rulesFilesTotal is short of the retained list' },
  )
  .refine(
    (tool) =>
      tool.rulesFilesTruncated === null ||
      tool.rulesFilesTotal === null ||
      tool.rulesFilesTruncated ===
        tool.rulesFilesTotal > tool.rulesFiles.length,
    {
      message:
        'rulesFilesTruncated disagrees with the retained list and the total',
    },
  );
export type ToolCompleteness = z.infer<typeof ToolCompletenessSchema>;

/**
 * Everywhere evidence was lost between the analyser and a reader.
 *
 * Counted per cause rather than summed, because the causes are not equally
 * recoverable and a reader acting on the number needs to know which one they
 * are looking at. All of them are zero on a capture nothing was lost from.
 *
 * `total` on the capture is the count BEFORE any of these, so
 * `total - retained` is fully explained by this record. A capture that cannot
 * account for its own shortfall is refused at storage.
 */
export const EvidenceLossSchema = z
  .object({
    /** Paths the scanner reported that are not repository-relative. */
    nonRelativePaths: z.number().int().nonnegative().default(0),
    /** Dropped to fit the graph blob that carries the list out of the scan. */
    beyondTransportCap: z.number().int().nonnegative().default(0),
    /** Dropped by the analyser's own retention bound. */
    beyondCaptureCap: z.number().int().nonnegative().default(0),
    /**
     * Dropped so the stored capture document fits the evidence store.
     *
     * The occurrence bound alone does not bound BYTES: a path may be 1,024
     * characters and a message 200, and the manifest carries one
     * length-prefixed content key per retained occurrence — so a capture well
     * inside the occurrence cap can exceed the store's per-document ceiling
     * and be permanently un-ingestible. That is the worst possible failure
     * here, because the graph is accepted first: the evidence is refused for
     * ever and recovery rebuilds the identical un-ingestible document.
     *
     * So the producer bounds the document and records what that cost, exactly
     * like every other cap. `0` on everything that fitted.
     */
    beyondDocumentBudget: z.number().int().nonnegative().default(0),
    /**
     * Dropped because a field exceeded what an occurrence may carry.
     *
     * A 1,100-character repository path is an ordinary path on disk and far
     * inside any byte budget, but the occurrence contract bounds a path at
     * `MAX_OCCURRENCE_PATH`. Such a finding used to travel all the way to
     * the ingest, which refused the whole capture — after the graph had been
     * accepted, so the refusal was permanent and every recovery rebuilt the
     * same payload that could not be stored.
     *
     * Dropped at the producer and COUNTED, because the alternative answers
     * are both false: failing the scan loses the findings that are fine, and
     * omitting it silently reports a repository as having less than it does.
     */
    unsupportedFields: z.number().int().nonnegative().default(0),
  })
  .readonly();
export type EvidenceLoss = z.infer<typeof EvidenceLossSchema>;

export function totalLost(loss: Readonly<EvidenceLoss>): number {
  return (
    loss.nonRelativePaths +
    loss.beyondTransportCap +
    loss.beyondCaptureCap +
    loss.beyondDocumentBudget +
    loss.unsupportedFields
  );
}

// ---------------------------------------------------------------------------
// The BYTE bound on a stored capture
// ---------------------------------------------------------------------------

/**
 * The evidence store's hard per-document ceiling.
 *
 * Convex refuses a document larger than 1,048,576 bytes. It is a hard limit,
 * not
 * a tuning knob: a capture above it cannot be written at all, at any later
 * date, by any retry.
 */
export const MAX_CAPTURE_DOCUMENT_BYTES = 1_048_576;

/**
 * Held back for everything on the row that is NOT the capture or its manifest.
 *
 * The tenant key, both identities, the commit, the run, the upload the graph
 * was accepted under, the storage ids, the binding state and the timestamps.
 * Every one is bounded and small; 16,384 bytes is far more than their sum and
 * leaves room for a field added later without silently re-opening the gap this
 * budget closes.
 */
export const CAPTURE_DOCUMENT_RESERVE_BYTES = 16_384;

/** What the capture and its manifest may occupy between them. */
export const CAPTURE_DOCUMENT_BUDGET_BYTES =
  MAX_CAPTURE_DOCUMENT_BYTES - CAPTURE_DOCUMENT_RESERVE_BYTES;

/**
 * Serialized UTF-8 length — the unit the store actually counts in.
 *
 * `String.length` counts UTF-16 code units, so it under-reports every
 * non-ASCII path and message and over-reports nothing. A budget measured in
 * characters would pass a capture of multibyte paths that the store then
 * refuses, which is precisely the failure being prevented.
 */
export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/**
 * The capture as the row STORES it: without its manifest.
 *
 * The manifest is a column in its own right, and for a large capture it is by
 * far the biggest thing on the row — one length-prefixed content key per
 * retained occurrence. Holding it twice, once as the column and once inside
 * the encoded capture, spent half the document on a byte-identical copy.
 *
 * Nothing is lost: the column IS the manifest, and `restoredCaptureJson` puts
 * it back on every read, so a reader parses exactly the capture that was
 * written.
 */
export function storedCaptureJson(capture: Readonly<FindingCapture>): string {
  return JSON.stringify({ ...capture, manifest: '' });
}

/** The stored capture with its manifest column put back. */
export function restoredCaptureJson(
  storedJson: string,
  manifest: string,
): string {
  const parsed = JSON.parse(storedJson) as Record<string, unknown>;
  return JSON.stringify({ ...parsed, manifest });
}

/**
 * The bytes one capture will occupy in the store: the manifest, plus the
 * capture encoded without it.
 *
 * Measured over what is ACTUALLY written, not estimated from the occurrence
 * count — a count says nothing about 1,024-character paths, multibyte
 * characters, or the per-tool configuration provenance a capture carries.
 */
export function captureDocumentBytes(
  capture: Readonly<FindingCapture>,
): number {
  return (
    utf8ByteLength(capture.manifest) +
    utf8ByteLength(storedCaptureJson(capture))
  );
}

/**
 * The row metadata the reserve is held for, and its own bounds.
 *
 * The reserve was a claim about fields nothing bounded. A `runId` is a string
 * and a string has no length, so a 32,768-character one consumed twice the
 * reserve on its own and a capture that measured as fitting did not fit. Each
 * of these is now bounded where it enters, and the sum is checked against the
 * reserve, so the budget's arithmetic is enforced rather than assumed.
 */
export const MAX_CAPTURE_RUN_ID = 256;
export const MAX_CAPTURE_UPLOAD_ID = 1024;
export const MAX_CAPTURE_REPO_KEY = 512;
export const MAX_CAPTURE_HEAD_SHA = 128;
export const MAX_CAPTURE_ID = 2048;
/**
 * An instant, at any precision anything real produces.
 *
 * `z.iso.datetime()` accepts an unbounded number of fractional digits, so a
 * "timestamp" could be 400,000 characters — and it is persisted three times
 * over: inside the capture, inside the manifest, and again as the row's own
 * `capturedAt` column. The budget measured the first two and covered the
 * third with a fixed allowance, so a capture with a short id and a vast
 * instant measured as fitting and did not fit.
 *
 * 64 bytes is far beyond `YYYY-MM-DDTHH:MM:SS.sssZ` with an offset and
 * nanosecond precision, and far short of anything that can distort a
 * document. Historical captures are unaffected: every instant any producer
 * has ever written is an order of magnitude inside it.
 */
export const MAX_CAPTURE_INSTANT = 64;

/**
 * Bytes the row's non-capture fields will occupy, measured as stored.
 *
 * Every identity is counted where it actually appears, INCLUDING the copies:
 * the capture id and repository key are on the row and inside the encoded
 * capture, and the upload id is recorded twice — as the expected producer and
 * as the binding. A fixed allowance covers the timestamps, the binding state,
 * the storage ids and JSON's own punctuation.
 */
export function captureRowMetadataBytes(
  metadata: Readonly<{
    captureId: string;
    repoKey: string;
    headSha: string;
    runId: string | null;
    uploadId: string | null;
    /** The instant the row indexes this capture under. */
    capturedAt?: string | undefined;
  }>,
): number {
  const fields = [
    metadata.captureId,
    metadata.repoKey,
    metadata.headSha,
    metadata.runId ?? '',
    metadata.uploadId ?? '',
    // The second copy of each identity the row carries.
    metadata.uploadId ?? '',
    // COUNTED, not covered by an allowance. It is a caller-supplied string
    // written to its own column and indexed, and an allowance is not a bound.
    metadata.capturedAt ?? '',
    // `recordedAt`, which the row stamps in the same shape.
    metadata.capturedAt ?? '',
  ];
  return (
    fields.reduce((sum, field) => sum + utf8ByteLength(field), 0) +
    CAPTURE_ROW_FIXED_BYTES
  );
}

/** Timestamps, binding state, storage ids, field names and punctuation. */
const CAPTURE_ROW_FIXED_BYTES = 2048;

/**
 * Refuse row metadata the reserve cannot hold, before anything is accepted.
 *
 * Each field is bounded in its own right — an over-long identity is a caller
 * fault worth naming — and their measured sum must fit the reserve the byte
 * budget subtracted for them.
 */
export function assertCaptureRowMetadataFits(
  metadata: Readonly<{
    captureId: string;
    repoKey: string;
    headSha: string;
    runId: string | null;
    uploadId: string | null;
    capturedAt?: string | undefined;
  }>,
): void {
  const bounds: readonly (readonly [string, number])[] = [
    [metadata.captureId, MAX_CAPTURE_ID],
    [metadata.repoKey, MAX_CAPTURE_REPO_KEY],
    [metadata.headSha, MAX_CAPTURE_HEAD_SHA],
    [metadata.runId ?? '', MAX_CAPTURE_RUN_ID],
    [metadata.uploadId ?? '', MAX_CAPTURE_UPLOAD_ID],
    [metadata.capturedAt ?? '', MAX_CAPTURE_INSTANT],
  ];
  if (bounds.some(([value, limit]) => utf8ByteLength(value) > limit)) {
    throw new TypeError('CAPTURE_ROW_METADATA_TOO_LARGE');
  }
  if (captureRowMetadataBytes(metadata) > CAPTURE_DOCUMENT_RESERVE_BYTES) {
    throw new TypeError('CAPTURE_ROW_METADATA_TOO_LARGE');
  }
}

/** Whether a capture can be durably stored at all. */
export function captureFitsDocumentBudget(
  capture: Readonly<FindingCapture>,
): boolean {
  return captureDocumentBytes(capture) <= CAPTURE_DOCUMENT_BUDGET_BYTES;
}

/**
 * Bytes the COMPLETE stored row will occupy: the capture and its manifest,
 * plus every identity the row carries beside them.
 *
 * The budget and the reserve are two halves of one number and were checked as
 * two independent things, which leaves a gap between them. A capture with a
 * 400,000-character id and no occurrences at all measures about 800 KB in
 * manifest-plus-capture — comfortably inside the budget — and then adds
 * another 400 KB as the row's own `captureId` column, putting the document
 * over the limit with nothing having exceeded a bound. Measuring the row is
 * what closes it.
 */
export function captureRowBytes(
  capture: Readonly<FindingCapture>,
  metadata: Readonly<{ runId: string | null; uploadId: string | null }>,
): number {
  return (
    captureDocumentBytes(capture) +
    captureRowMetadataBytes({
      captureId: capture.captureId,
      repoKey: capture.repoKey,
      headSha: capture.headSha,
      runId: metadata.runId,
      uploadId: metadata.uploadId,
      capturedAt: capture.capturedAt,
    })
  );
}

/**
 * Refuse a row the store cannot hold, whatever measured as fitting.
 *
 * Both halves, in the order that names the cause most precisely: an identity
 * past its own bound is a caller fault worth naming as one; anything else
 * that pushes the complete row over the limit is refused on the measurement.
 */
export function assertCaptureRowFits(
  capture: Readonly<FindingCapture>,
  metadata: Readonly<{ runId: string | null; uploadId: string | null }>,
): void {
  assertCaptureRowMetadataFits({
    captureId: capture.captureId,
    repoKey: capture.repoKey,
    headSha: capture.headSha,
    runId: metadata.runId,
    uploadId: metadata.uploadId,
    capturedAt: capture.capturedAt,
  });
  if (!captureFitsDocumentBudget(capture)) {
    throw new TypeError('CAPTURE_DOCUMENT_TOO_LARGE');
  }
  if (captureRowBytes(capture, metadata) > MAX_CAPTURE_DOCUMENT_BYTES) {
    throw new TypeError('CAPTURE_DOCUMENT_TOO_LARGE');
  }
}

/**
 * How complete a capture is, stated rather than inferred.
 *
 * `complete` requires every configured tool to have reported `ok` AND nothing
 * to have been dropped by a cap. Anything else is `bounded` with a named
 * reason, and a capture with no tool record at all is `unknown` — the case a
 * comparison must refuse rather than guess at.
 */
export const CaptureCompletenessSchema = z
  .object({
    kind: z.enum(['complete', 'bounded', 'unknown']),
    /** `null` when nothing was capped. */
    cap: z.number().int().positive().nullable().default(null),
    /** Machine-readable: `tools-skipped`, `tools-failed`, `capture-capped`, … */
    reasons: z.array(z.string().min(1)).default([]),
  })
  .readonly();
export type CaptureCompleteness = z.infer<typeof CaptureCompletenessSchema>;

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * The STABLE identity of a problem: repository, rule and file.
 *
 * Not the line and not the message. A finding that moved because an import was
 * added above it is the same problem, and treating it as new would report a
 * regression that never happened — which is the single most damaging thing a
 * scan-over-scan comparison can do.
 */
export function findingIdFor(
  input: Readonly<{ repoKey: string; ruleId: string; path: string }>,
): string {
  return [
    'find-v1',
    canonicalString(input.repoKey.toLowerCase()),
    canonicalString(input.ruleId),
    canonicalString(input.path),
  ].join(':');
}

/** Whether a stable finding id belongs to a repository, without parsing it. */
export function findingIdBelongsTo(
  findingId: string,
  repoKey: string,
): boolean {
  return findingId.startsWith(
    `find-v1:${canonicalString(repoKey.toLowerCase())}:`,
  );
}

/**
 * A total order over ONE occurrence's content.
 *
 * Every field the analyser reported, length-prefixed and null-aware. A partial
 * key leaves ties that two runs may break differently, and a tie broken the
 * other way renumbers the ordinals and turns an honest re-upload into a
 * conflict.
 */
export function occurrenceContentKey(
  occurrence: Readonly<{
    ruleId: string;
    path: string;
    line: number | null;
    column: number | null;
    message: string | null;
    severity: string;
    category: string;
    suppressionScope: string | null;
    /**
     * The analyser that reported it.
     *
     * Part of the content BECAUSE it now decides a verdict. A finding's tool
     * selects which rules-provenance applies to it, so swapping the tool on
     * two findings — one from an observed analyser, one from an undecided
     * one, at different paths, with each tool's totals unchanged — turned an
     * unreadable finding into a readable one and back. Every id and the
     * manifest were identical, so the store accepted the swap as a fresh
     * write and called a replay of it a match.
     *
     * Deliberately NOT part of `findingId`: that is the PROBLEM's identity —
     * repository, rule and path — and keeping the tool out of it is what
     * lets the same problem stay one problem as its line moves. Occurrence
     * identity is a sighting, and a sighting by a different analyser is a
     * different sighting.
     *
     * Backwards compatibility, explicitly: this changes every occurrence id
     * and manifest minted from now on. Captures stored before it keep the
     * ids they were stored with and stay readable and comparable; a re-upload
     * of one of those scans is a genuinely different capture and is stored as
     * such rather than silently matching. Nothing recomputes an old id.
     */
    tool?: string | null | undefined;
  }>,
  encoding: OccurrenceEncoding = FINDING_CAPTURE_VERSION,
): string {
  return [
    canonicalString(occurrence.ruleId),
    canonicalString(occurrence.path),
    canonicalOptional(
      occurrence.line === null ? null : String(occurrence.line),
    ),
    canonicalOptional(
      occurrence.column === null ? null : String(occurrence.column),
    ),
    canonicalOptional(occurrence.message),
    canonicalString(occurrence.severity),
    canonicalString(occurrence.category),
    canonicalOptional(occurrence.suppressionScope),
    // Only under the encoding that HAS it. Appending it to a `1` key would
    // change every legacy id, so the original bundle could never replay.
    ...(encoding >= OCCURRENCE_ENCODING_V2
      ? [canonicalOptional(occurrence.tool ?? null)]
      : []),
  ].join(':');
}

/**
 * The identity of ONE occurrence inside ONE capture.
 *
 * It names the capture, so it is immutable and scan-specific by construction:
 * the same rule on the same line of the same file in two scans yields two
 * different occurrence ids, because they are two sightings and a decision
 * pinned to one must not silently re-point at the other.
 *
 * It also names the occurrence's own CONTENT, so two hits of one rule in one
 * file — different lines, different messages — are two ids rather than one.
 * The trailing ordinal separates hits that are genuinely indistinguishable:
 * those are still two places a person has to look, and collapsing them would
 * under-report the work by exactly the amount that is hardest to notice.
 */
export function occurrenceIdFor(
  input: Readonly<{
    captureId: string;
    findingId: string;
    contentKey: string;
    ordinal: number;
  }>,
): string {
  return [
    'occ-v1',
    canonicalString(input.captureId),
    canonicalString(input.findingId),
    canonicalString(input.contentKey),
    canonicalString(String(input.ordinal)),
  ].join(':');
}

/** The identity of one collection run: repository, commit, instant, config. */
export function captureIdFor(
  input: Readonly<{
    repoKey: string;
    headSha: string;
    capturedAt: string;
    configVersion: string;
    /** Defaults to what new captures are minted under. */
    encoding?: OccurrenceEncoding | undefined;
  }>,
): string {
  // The encoding is part of the id, so the SAME scan re-encoded is a
  // different capture rather than the same one carrying a manifest that no
  // longer matches — a conflict with no correct resolution.
  return [
    `cap-v${String(input.encoding ?? FINDING_CAPTURE_VERSION)}`,
    canonicalString(input.repoKey.toLowerCase()),
    canonicalString(input.headSha),
    canonicalString(input.capturedAt),
    canonicalString(input.configVersion),
  ].join(':');
}

/**
 * The fingerprint of a capture's COMPLETE retained occurrence multiset, plus
 * the context it was taken in.
 *
 * Comparing incoming occurrences one at a time cannot see what is MISSING. A
 * re-upload that drops rule A and adds rule B has the same count and the same
 * categories; every row looks unseen, all of them insert, and A is left
 * standing beside B — a stored capture no scan ever produced. Comparing the
 * whole multiset makes a removal exactly as loud as an addition.
 *
 * Order-independent, so a re-upload in a different sequence is the same
 * capture and writes nothing. An EMPTY capture has a manifest too: "this scan
 * found nothing" is a claim, and only a stored claim can be contradicted.
 */
/**
 * One nullable field of the manifest, tagged under `3` and frozen before it.
 *
 * `1` and `2` wrote a sentinel string, which a real value could equal; `3`
 * writes `canonicalOptional`, where `null` and every string are structurally
 * distinct. The older spelling is kept exactly so a stored capture still
 * recomputes to what it was written as — the only thing that can repair a
 * pending binding is the original bundle.
 */
function optionalField(
  value: string | null,
  legacySentinel: string,
  encoding: OccurrenceEncoding,
): string {
  if (encoding >= OCCURRENCE_ENCODING_V3) return canonicalOptional(value);
  return value ?? legacySentinel;
}

/**
 * One tool's record inside the manifest.
 *
 * Each field is length-prefixed in its own right: a tool literally named
 * `eslint~ok~3` must not encode like eslint having run with three findings.
 * The version is part of it, so a rule set moving under a fixed tool name
 * changes the manifest.
 *
 * Everything past the version exists only under the LATER encodings. A `1`
 * manifest must come out byte for byte as it did when it was written, or the
 * original bundle — the only thing that can repair a pending binding — no
 * longer matches its own stored manifest and is refused.
 *
 * Under `2` and later: a capture that observed a tool's rules and one that did
 * not are two different readings, and encoding them alike would let a
 * comparison treat an undecided configuration as the pinned one it is compared
 * against. And the per-file record could otherwise be rewritten — a state
 * flipped, an entry dropped — with every id and the manifest still matching.
 */
function toolField(
  tool: Readonly<{
    evidence?: ScannerToolEvidence | undefined;
    name: string;
    status: string;
    findingCount: number;
    version?: string | null | undefined;
    rulesObserved?: boolean | null | undefined;
    rulesFiles?:
      | readonly Readonly<{
          path: string;
          state: string;
          sha256?: string | null | undefined;
        }>[]
      | undefined;
    rulesFilesTotal?: number | null | undefined;
    rulesFilesTruncated?: boolean | null | undefined;
  }>,
  encoding: OccurrenceEncoding,
): string {
  const flag = (value: boolean | null | undefined, legacy: string): string =>
    optionalField(
      value === null || value === undefined ? null : String(value),
      legacy,
      encoding,
    );
  return canonicalList([
    tool.name,
    tool.status,
    String(tool.findingCount),
    // Absent historical observations preserve their exact encoding. Zod's
    // schema-declaration field order (including nested evidence objects) is
    // part of this identity: never reorder/add defaults without an encoding
    // migration. Input object insertion order is intentionally irrelevant.
    ...(tool.evidence === undefined
      ? []
      : [
          canonicalString(
            JSON.stringify(ScannerToolEvidenceSchema.parse(tool.evidence)),
          ),
        ]),
    // Under `3` a type tag, so no string can encode like the absence of one.
    // Frozen as a sentinel for `1` and `2`, which must recompute byte for
    // byte as they were stored.
    encoding >= OCCURRENCE_ENCODING_V3
      ? canonicalOptional(tool.version ?? null)
      : (tool.version ?? LEGACY_UNKNOWN_VERSION),
    ...(encoding >= OCCURRENCE_ENCODING_V2
      ? [
          flag(tool.rulesObserved, LEGACY_RULES_UNRECORDED),
          canonicalList([
            optionalField(
              tool.rulesFilesTotal === null ||
                tool.rulesFilesTotal === undefined
                ? null
                : String(tool.rulesFilesTotal),
              LEGACY_TOTAL_UNRECORDED,
              encoding,
            ),
            flag(tool.rulesFilesTruncated, LEGACY_TRUNCATION_UNRECORDED),
            canonicalList(
              [...(tool.rulesFiles ?? [])]
                .map((file) =>
                  canonicalList([
                    file.path,
                    file.state,
                    optionalField(
                      file.sha256 ?? null,
                      LEGACY_NO_HASH,
                      encoding,
                    ),
                  ]),
                )
                .toSorted(),
            ),
          ]),
        ]
      : []),
  ]);
}

export function captureManifestFor(
  input: Readonly<{
    captureId: string;
    repoKey: string;
    headSha: string;
    capturedAt: string;
    analyzerVersion: string | null;
    configVersion: string;
    evidenceProfile?: EvidenceProfile | undefined;
    sourceCoverage?: SourceCoverage | undefined;
    completeness: CaptureCompleteness;
    /**
     * `rulesObserved` is optional HERE only: callers that predate it (and
     * the tests that pin older manifests) encode exactly as a capture whose
     * observer said nothing, which is what `null` already means.
     */
    tools: readonly (Omit<
      ToolCompleteness,
      'rulesObserved' | 'rulesFiles' | 'rulesFilesTotal' | 'rulesFilesTruncated'
    > & {
      readonly rulesObserved?: boolean | null | undefined;
      readonly rulesFiles?:
        | readonly Readonly<{
            path: string;
            state: string;
            sha256?: string | null | undefined;
          }>[]
        | undefined;
      readonly rulesFilesTotal?: number | null | undefined;
      readonly rulesFilesTruncated?: boolean | null | undefined;
    })[];
    total: number;
    loss: EvidenceLoss;
    contentKeys: readonly string[];
  }>,
  encoding: OccurrenceEncoding = FINDING_CAPTURE_VERSION,
): string {
  const context = [
    canonicalString(input.captureId),
    canonicalString(input.repoKey.toLowerCase()),
    canonicalString(input.headSha),
    canonicalString(input.capturedAt),
    canonicalOptional(input.analyzerVersion),
    canonicalString(input.configVersion),
    ...(input.evidenceProfile === undefined
      ? []
      : [canonicalString(input.evidenceProfile)]),
    ...(input.sourceCoverage === undefined
      ? []
      : [
          canonicalString(
            JSON.stringify(SourceCoverageSchema.parse(input.sourceCoverage)),
          ),
        ]),
    canonicalString(input.completeness.kind),
    canonicalOptional(
      input.completeness.cap === null ? null : String(input.completeness.cap),
    ),
    canonicalList([...input.completeness.reasons].toSorted()),
    // Each tool's fields length-prefixed individually: a tool literally named
    // `eslint~ok~3` must not encode like eslint having run with three
    // findings. The version is part of it, so a rule set moving under a fixed
    // tool name changes the manifest.
    canonicalList(
      [...input.tools].map((tool) => toolField(tool, encoding)).toSorted(),
    ),
    canonicalString(String(input.total)),
    // The loss record is part of the capture's identity. Without it a capture
    // that dropped 509 findings and one that dropped none could share a
    // manifest whenever the retained sets happened to match.
    // The fourth component is APPENDED ONLY WHEN NON-ZERO, so every manifest
    // written before the document budget existed — all of which dropped
    // nothing to it — recomputes byte for byte as it was stored. A three-list
    // and a four-list can never collide: `canonicalList` encodes the count.
    canonicalList([
      String(input.loss.nonRelativePaths),
      String(input.loss.beyondTransportCap),
      String(input.loss.beyondCaptureCap),
      ...(input.loss.beyondDocumentBudget > 0 ||
      input.loss.unsupportedFields > 0
        ? [String(input.loss.beyondDocumentBudget)]
        : []),
      // Appended only when it bit, for the same compatibility reason as the
      // component above it: a manifest written before either existed must
      // recompute exactly as it was stored.
      ...(input.loss.unsupportedFields > 0
        ? [String(input.loss.unsupportedFields)]
        : []),
    ]),
  ].join(':');
  const entries = [...input.contentKeys]
    .toSorted()
    .map((key) => canonicalString(key))
    .join(',');
  return [
    `manifest-v${String(encoding)}`,
    canonicalString(context),
    canonicalString(String(input.contentKeys.length)),
    entries,
  ].join(':');
}

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/**
 * What an occurrence may carry, as NUMBERS the producer can enforce too.
 *
 * They were inline in the schema, so the only thing that knew them was the
 * parse at the far end of the pipeline — which is after the graph has been
 * accepted, where a refusal is permanent.
 */
export const MAX_OCCURRENCE_PATH = 1024;
export const MAX_OCCURRENCE_RULE_ID = 128;
export const MAX_OCCURRENCE_TOOL = 64;
export const MAX_OCCURRENCE_MESSAGE = 200;

export function isRepositoryPath(path: string): boolean {
  return (
    path.length > 0 &&
    !/[\\:\u0000-\u001F\u007F]/u.test(path) &&
    path
      .split('/')
      .every((part) => part !== '' && part !== '.' && part !== '..')
  );
}

/** One sighting of one problem, exactly as the analyser reported it. */
export const FindingOccurrenceSchema = z
  .object({
    occurrenceId: z.string().min(1),
    /** The stable problem this sighting belongs to. */
    findingId: z.string().min(1),
    captureId: z.string().min(1),
    repoKey: z.string().min(1),
    /** The commit this sighting was taken at. Provenance, not identity. */
    headSha: z.string().min(1),
    ruleId: z.string().min(1).max(MAX_OCCURRENCE_RULE_ID),
    /** Repository-relative. An absolute path here would be a scanner leak. */
    path: z.string().min(1).max(MAX_OCCURRENCE_PATH).refine(isRepositoryPath),
    severity: FindingSeveritySchema,
    category: FindingCategorySchema,
    line: z.number().int().positive().nullable().default(null),
    column: z.number().int().positive().nullable().default(null),
    message: z.string().max(MAX_OCCURRENCE_MESSAGE).nullable().default(null),
    suppressionScope: z.enum(SUPPRESSION_SCOPES).nullable().default(null),
    /**
     * Which analyser reported it, when the producer said. `null` is unknown
     * and never a placeholder for "none".
     */
    tool: z.string().min(1).max(MAX_OCCURRENCE_TOOL).nullable().default(null),
    /** Separates genuinely indistinguishable hits. Capture-local. */
    ordinal: z.number().int().nonnegative(),
    capturedAt: z.iso.datetime(),
  })
  .readonly();
export type FindingOccurrence = z.infer<typeof FindingOccurrenceSchema>;

/** The measurement context one capture was taken in. */
/**
 * How many dimension groups a capture may CARRY.
 *
 * A bound, because the group count is a property of the scanned repository,
 * not of this code: one tool with nine hundred distinct rules fires nine
 * hundred groups, and an unbounded list inside a bounded document is the
 * same refused-capture failure the occurrence cap exists to prevent. When
 * the bound bites it is reported, with the true group total beside it, rather
 * than silently cutting the tail.
 */
export const MAX_CAPTURE_DIMENSION_GROUPS = 250;

/**
 * ONE (tool, severity, rule, file class) cell, counted both sides of the cap.
 *
 * `produced` and `retained` are occurrence counts in the same unit, taken
 * from the same findings by the same classifier, so `produced - retained` is
 * the number of occurrences in this cell that the capture does not hold.
 * Counting both sides the same way is the point: the one diagnosed case of
 * this going wrong subtracted per-file metric records from occurrences, and
 * a subtraction across units cannot be interpreted at all. That case is an
 * explanation of itself, not a general reading of any other mismatch.
 */
export const CaptureDimensionGroupSchema = z
  .object({
    tool: z.string().min(1).max(64).nullable(),
    severity: FindingSeveritySchema,
    rule: z.string().min(1).max(MAX_OCCURRENCE_RULE_ID).nullable(),
    fileClass: FindingFileClassSchema,
    produced: z.number().int().nonnegative(),
    retained: z.number().int().nonnegative(),
  })
  .readonly();
export type CaptureDimensionGroup = z.infer<typeof CaptureDimensionGroupSchema>;

/**
 * Produced versus retained findings BY DIMENSION, measured at the producer.
 *
 * Measured where the uncapped list still exists, which is the only place it
 * can be: once a capture is stored, the occurrences beyond its cap are gone
 * and no downstream reader can reconstruct what they were. A shortlist can
 * then say which dimensions it is not showing instead of reporting the
 * historical `unavailable`.
 *
 * Deliberately NOT part of the manifest, the capture id, or the recovery
 * digest. Those three are the capture's identity and must recompute byte for
 * byte for every capture ever stored; a measurement added beside them cannot
 * be allowed to move them, and binding it would have meant a new encoding
 * version for every reader of every historical capture.
 *
 * WHAT THAT COSTS, stated rather than hidden. The boundary this record
 * actually crosses is an authenticated one: `recordFindingCapture` is an
 * org-scoped `authedMutation`, and on the upload path `storeCapturePart`
 * derives the payload digest from the bytes that arrived and rebuilds the
 * capture with the producer's own builder rather than trusting the caller's.
 * The refinements below then reconcile this record against the capture's own
 * `total` and `retained` before anything is stored, so numbers that cannot be
 * squared with the capture are refused.
 *
 * What remains unchecked is the step from findings to counts. The manifest
 * commits to the occurrence multiset and the tool records; it does not commit
 * to this field, so a holder of the write credential could assert
 * self-consistent counts no scan produced, and no per-group cross-check
 * against the retained occurrences exists either — the schema validates the
 * capture without them. `manifestCovered: false` is a literal, so it cannot
 * be flipped, and readers present this as producer-reported and advisory
 * rather than as verified produced proof.
 *
 * Absent on every capture written before this existed, and absent too when
 * the document budget could not afford it. Absent means UNAVAILABLE — never
 * zero, and never a licence to say a dimension was empty.
 */
export const CaptureDimensionsSchema = z
  .object({
    /** The unit BOTH sides are counted in. Never records, never files. */
    unit: z.literal('occurrences'),
    /** Where the measurement was taken, relative to the caps. */
    observedAt: z.literal('producer-pre-cap'),
    /** The path heuristic that produced `fileClass`, by name. */
    classification: z.literal(FINDING_PATH_CLASSIFICATION),
    /** False, always, and stated: see the note above. */
    manifestCovered: z.literal(false),
    groups: z
      .array(CaptureDimensionGroupSchema)
      .max(MAX_CAPTURE_DIMENSION_GROUPS),
    /** Distinct groups the producer SAW, whether or not they are listed. */
    groupsTotal: z.number().int().nonnegative(),
    /** Whether `groups` is short of `groupsTotal`, for any reason. */
    groupsTruncated: z.boolean(),
    /**
     * Distinct groups NOT listed because the contract cannot carry their
     * identity — a tool or rule id longer than an occurrence may hold.
     *
     * Counted rather than shortened. Listing them would mean cutting the
     * identity to fit, and a cut identity is a prefix: two rule ids sharing
     * one would merge into a single row, under-reporting the real group count
     * while the list claimed to be complete. So they are disclosed as omitted
     * and their occurrences accounted for separately below, which keeps the
     * listed sums interpretable without inventing an identity for anything.
     */
    unrepresentedGroups: z.number().int().nonnegative(),
    /** Produced occurrences inside those omitted groups. */
    unrepresentedProduced: z.number().int().nonnegative(),
    /** Retained occurrences inside those omitted groups. */
    unrepresentedRetained: z.number().int().nonnegative(),
    /** Occurrences the producer could classify, summed over every group. */
    producedObserved: z.number().int().nonnegative(),
    /** Of those, how many the capture retained. */
    retainedObserved: z.number().int().nonnegative(),
    /**
     * Occurrences whose dimensions CANNOT be measured here.
     *
     * A transport that cut the list before the producer saw it leaves counts
     * the producer can total but never classify, so they are reported as
     * their own unmeasurable quantity rather than distributed across groups
     * or quietly dropped. `producedObserved + unobserved` is the capture's
     * own `total`.
     */
    unobserved: z.number().int().nonnegative(),
  })
  .readonly()
  // The listed groups are a SUBSET of what was seen, never a superset.
  .refine((d) => d.groupsTotal >= d.groups.length, {
    message: 'groupsTotal is short of the listed groups',
  })
  .refine((d) => d.groupsTruncated === d.groupsTotal > d.groups.length, {
    message: 'groupsTruncated contradicts the listed group count',
  })
  // One cell per dimension tuple. A repeated tuple makes every sum below
  // ambiguous and lets the same occurrences be counted twice.
  .refine(
    (d) =>
      new Set(
        d.groups.map((g) =>
          JSON.stringify([g.tool, g.severity, g.rule, g.fileClass]),
        ),
      ).size === d.groups.length,
    { message: 'A dimension group is repeated' },
  )
  // A cell cannot retain more than it produced.
  .refine((d) => d.groups.every((g) => g.retained <= g.produced), {
    message: 'A dimension group retained more than it produced',
  })
  // The sums must ACCOUNT for truncation rather than ignore it: equality
  // when the list is whole, at most when its tail was cut. Without this a
  // record could list one group of one and claim thousands observed.
  // The listed groups and the omitted ones are disjoint subsets of the total.
  .refine((d) => d.groups.length + d.unrepresentedGroups <= d.groupsTotal, {
    message: 'Listed and unrepresented groups exceed the group total',
  })
  .refine((d) => d.unrepresentedRetained <= d.unrepresentedProduced, {
    message: 'Unrepresented groups retained more than they produced',
  })
  // The sums must ACCOUNT for everything dropped rather than ignore it:
  // equality once the omitted groups are added back when the list is
  // otherwise whole, at most when the cap also cut its tail. Without this a
  // record could list one group of one and claim thousands observed.
  .refine(
    (d) =>
      d.groupsTruncated
        ? sumOf(d.groups, 'produced') + d.unrepresentedProduced <=
          d.producedObserved
        : sumOf(d.groups, 'produced') + d.unrepresentedProduced ===
          d.producedObserved,
    { message: 'Listed produced counts do not account for the group total' },
  )
  .refine(
    (d) =>
      d.groupsTruncated
        ? sumOf(d.groups, 'retained') + d.unrepresentedRetained <=
          d.retainedObserved
        : sumOf(d.groups, 'retained') + d.unrepresentedRetained ===
          d.retainedObserved,
    { message: 'Listed retained counts do not account for the group total' },
  )
  // What is retained is a subset of what was produced, in aggregate too.
  .refine((d) => d.retainedObserved <= d.producedObserved, {
    message: 'retainedObserved exceeds producedObserved',
  });
export type CaptureDimensions = z.infer<typeof CaptureDimensionsSchema>;

function sumOf(
  groups: readonly Readonly<Record<'produced' | 'retained', number>>[],
  side: 'produced' | 'retained',
): number {
  return groups.reduce((total, group) => total + group[side], 0);
}

export const FindingCaptureSchema = z
  .object({
    captureId: z.string().min(1),
    repoKey: z.string().min(1),
    headSha: z.string().min(1),
    capturedAt: z.iso.datetime().max(MAX_CAPTURE_INSTANT),
    /** Which scanner produced it, or `null` when the scan did not say. */
    analyzerVersion: z.string().min(1).nullable().default(null),
    /** The signal set, so two unlike scans are never compared as one. */
    configVersion: z.string().min(1),
    /** Per tool, because the suite's aggregate cannot answer per category. */
    tools: z.array(ToolCompletenessSchema).default([]),
    evidenceProfile: EvidenceProfileSchema.optional(),
    sourceCoverage: SourceCoverageSchema.optional(),
    completeness: CaptureCompletenessSchema,
    /**
     * How many occurrences the ANALYSER produced, before anything was
     * dropped anywhere — including by the transport that carries the list out
     * of the scan process.
     *
     * A count taken after a slice is not a total, and reporting one as a total
     * is how a repository with 1,009 findings comes to be described as
     * completely analysed with 500.
     */
    total: z.number().int().nonnegative(),
    /** How many are stored. */
    retained: z.number().int().nonnegative(),
    /** Where the difference went. `total - retained` must equal its sum. */
    loss: EvidenceLossSchema,
    /**
     * Produced versus retained by dimension, when the producer measured it.
     *
     * Optional because no capture written before this field existed has it,
     * and a default would manufacture counts for scans nobody measured.
     */
    dimensions: CaptureDimensionsSchema.optional(),
    manifest: z.string().min(1),
    // BOTH encodings are readable. A capture stored under `1` stays valid
    // evidence and stays replayable; only new ones are minted under `2`.
    version: z.union([
      z.literal(OCCURRENCE_ENCODING_V1),
      z.literal(OCCURRENCE_ENCODING_V2),
      z.literal(OCCURRENCE_ENCODING_V3),
      z.literal(OCCURRENCE_ENCODING_V4),
    ]),
  })
  .readonly()
  // ---------------------------------------------------------------------
  // The dimension record must RECONCILE with the capture carrying it.
  //
  // Checked here because here is the authenticated write boundary: the
  // ingest parses with this schema before it stores anything, so a record
  // whose counts cannot be squared with the capture's own `total` and
  // `retained` is refused rather than stored and later presented as a
  // measurement. Without these two, the record was accepted on its own say-so
  // and any number at all could be asserted beside a valid manifest.
  //
  // Skipped entirely when the field is absent, which is every capture written
  // before it existed. Absent is unavailable, never zero.
  // ---------------------------------------------------------------------
  .refine(
    (capture) =>
      capture.dimensions === undefined ||
      capture.dimensions.producedObserved + capture.dimensions.unobserved ===
        capture.total,
    {
      message:
        'Dimension produced/unobserved counts do not sum to the capture total',
    },
  )
  .refine(
    (capture) =>
      capture.dimensions === undefined ||
      capture.dimensions.retainedObserved === capture.retained,
    { message: 'Dimension retained count contradicts the capture' },
  );
export type FindingCapture = z.infer<typeof FindingCaptureSchema>;

/** A capture plus the occurrences it retained. The producer's whole output. */
export const FindingCaptureBundleSchema = z
  .object({
    capture: FindingCaptureSchema,
    occurrences: z.array(FindingOccurrenceSchema).max(MAX_CAPTURE_OCCURRENCES),
  })
  .readonly();
export type FindingCaptureBundle = z.infer<typeof FindingCaptureBundleSchema>;

/**
 * Whether a capture supports a comparison at all.
 *
 * `unknown` completeness never does: a scan that cannot say which tools ran
 * cannot establish that a finding is gone rather than unlooked-for.
 */
export function isComparableCapture(
  capture: Readonly<Pick<FindingCapture, 'completeness'>>,
): boolean {
  return capture.completeness.kind !== 'unknown';
}
